/**
 * Parse AppSync globalsubscribe payloads and drive web refresh/navigation.
 *
 * MessageType values (Type: Refresh):
 * - Pilot Profile → mobile only (ignored on web)
 * - Task → refresh tasks list
 * - Mission → refresh mission list / mission detail (Data may be missionId)
 * - MissionId → navigate to mission details (Data is missionId)
 */

export const GLOBAL_DATA_REFRESH_EVENT = "globalDataRefresh";

const WEB_REFRESH_TYPES = new Set(["task", "mission", "missionid"]);

function normalizeToken(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export function parseGlobalRefreshEvent(message) {
  if (!message || typeof message !== "object") return null;

  const messageTypeRaw = message.MessageType ?? message.messageType ?? "";
  const actionTypeRaw = message.Type ?? message.type ?? "";
  const messageType = normalizeToken(messageTypeRaw);
  const actionType = normalizeToken(actionTypeRaw);

  if (actionType !== "refresh") return null;

  const dataRaw = message.Data ?? message.data;
  const dataId =
    dataRaw == null || dataRaw === "" ? null : String(dataRaw).trim() || null;

  return {
    messageType,
    messageTypeRaw: String(messageTypeRaw).trim(),
    actionType,
    dataId,
    message: String(message.Message ?? message.message ?? "").trim(),
    eventId: message.EventId ?? message.eventId ?? null,
    raw: message,
    handleOnWeb: WEB_REFRESH_TYPES.has(messageType),
    isPilotProfile:
      messageType === "pilotprofile" || messageType === "pilot",
  };
}

export function dispatchGlobalDataRefresh(detail) {
  window.dispatchEvent(
    new CustomEvent(GLOBAL_DATA_REFRESH_EVENT, { detail }),
  );
}

export function shouldUseDataAsNotificationBody(parsed) {
  if (!parsed) return true;
  if (parsed.dataId && WEB_REFRESH_TYPES.has(parsed.messageType)) {
    return false;
  }
  return true;
}

/**
 * Web-side reactions to Refresh events (also dispatches globalDataRefresh).
 */
export function applyWebRefreshFromSubscription(message, navigate) {
  const parsed = parseGlobalRefreshEvent(message);
  if (!parsed || parsed.isPilotProfile) return parsed;

  if (parsed.handleOnWeb) {
    dispatchGlobalDataRefresh(parsed);
  }

  if (!navigate) return parsed;

  if (parsed.messageType === "missionid" && parsed.dataId) {
    navigate(`/dashboard/missions/${parsed.dataId}`);
    return parsed;
  }

  if (parsed.messageType === "mission" && parsed.dataId) {
    const onMissionDetail = window.location.pathname.includes(
      `/dashboard/missions/${parsed.dataId}`,
    );
    if (!onMissionDetail) {
      navigate(`/dashboard/missions/${parsed.dataId}`);
    }
    return parsed;
  }

  if (parsed.messageType === "task") {
    if (!window.location.pathname.startsWith("/dashboard/tasks")) {
      navigate("/dashboard/tasks");
    }
  }

  return parsed;
}
